import type { ComponentType } from 'react'
import { useEffect, useRef } from 'react'
import { Card } from '@cocoonspace/ui/components/card'
import { cn } from '@cocoonspace/ui/lib/utils'
import { AnimatePresence, motion } from 'motion/react'

import { SchedulerCalendarProvider, useSchedulerCalendar } from './provider'
import { SchedulerCalendarHeader } from './scheduler-calendar-header'
import { ViewMonth } from './view-month'
import { ViewWeek } from './view-week'
import type {
	SchedulerCalendarEvent,
	SchedulerCalendarEventComponentProps,
	SchedulerCalendarEventPreviewProps,
	SchedulerCalendarLocale,
	SchedulerCalendarView,
	SchedulerVisiblePeriod,
	RenderSelectedEvent,
} from './types'

type SchedulerCalendarProps<T> = {
	className?: string
	events: SchedulerCalendarEvent<T>[]
	EventComponent?: ComponentType<SchedulerCalendarEventComponentProps<T>>
	EventPreview?: ComponentType<SchedulerCalendarEventPreviewProps<T>>
	onSelectEvent?: (event: SchedulerCalendarEvent<T> | null) => void
	onVisiblePeriodChange?: (period: SchedulerVisiblePeriod) => void
	locale?: SchedulerCalendarLocale
	weekStartHour?: number
	weekEndHour?: number
	defaultView?: SchedulerCalendarView
	renderSelectedEvent?: RenderSelectedEvent<T>
}

const SchedulerCalendarContent = ({
	className,
	onVisiblePeriodChange,
}: {
	className?: string
	onVisiblePeriodChange?: (period: SchedulerVisiblePeriod) => void
}) => {
	const { view, visiblePeriod, EventPreview, selectedEvent } =
		useSchedulerCalendar()
	const lastPeriodKeyRef = useRef<string | null>(null)

	useEffect(() => {
		if (!onVisiblePeriodChange) return
		if (!visiblePeriod) return

		const nextKey = `${view}-${visiblePeriod.start.getTime()}-${visiblePeriod.end.getTime()}`
		if (lastPeriodKeyRef.current === nextKey) return

		lastPeriodKeyRef.current = nextKey
		onVisiblePeriodChange(visiblePeriod)
	}, [onVisiblePeriodChange, visiblePeriod, view])

	return (
		<Card className={cn('flex max-h-[400px] flex-col', className)}>
			<SchedulerCalendarHeader />
			{view === 'week' && <ViewWeek />}
			{view === 'month' && <ViewMonth />}

			<AnimatePresence>
				{!!selectedEvent && (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 10 }}
						transition={{ duration: 0.2 }}
						className='absolute right-4 bottom-4 z-10'
					>
						<EventPreview event={selectedEvent} />
					</motion.div>
				)}
			</AnimatePresence>
		</Card>
	)
}

export const SchedulerCalendar = <T,>({
	className,
	events,
	onVisiblePeriodChange,
	EventComponent,
	EventPreview,
	onSelectEvent,
	locale = 'fr',
	weekStartHour = 8,
	weekEndHour = 23,
	defaultView = 'week',
	renderSelectedEvent,
}: SchedulerCalendarProps<T>) => (
	<SchedulerCalendarProvider
		renderSelectedEvent={renderSelectedEvent as RenderSelectedEvent<unknown>}
		defaultView={defaultView}
		events={events as SchedulerCalendarEvent[]}
		EventComponent={
			EventComponent as ComponentType<SchedulerCalendarEventComponentProps>
		}
		EventPreview={
			EventPreview as ComponentType<SchedulerCalendarEventPreviewProps>
		}
		onSelectEvent={
			onSelectEvent as
				| ((event: SchedulerCalendarEvent | null) => void)
				| undefined
		}
		locale={locale}
		weekStartHour={weekStartHour}
		weekEndHour={weekEndHour}
	>
		<SchedulerCalendarContent
			className={className}
			onVisiblePeriodChange={onVisiblePeriodChange}
		/>
	</SchedulerCalendarProvider>
)
